import React, { useState } from 'react';
import { Modal, Typography, DatePicker, Select, theme } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { message } from '../../services/toastMessage';
import { confirmServiceVisit } from '../../services/ServiceRequestService';
import { getMyServices } from '../../services/ServiceService';
import { IService } from '../../interfaces/heavyDutyMachinery/IService';
import { IServiceRequest } from '../../interfaces/heavyDutyMachinery/IServiceRequest';
import { latestServiceQuote } from '../../helpers/serviceRequestHelper';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { formatCurrency } from '../../helpers/currencyHelper';
import { SURFACE_1, TEAL } from '../../theme/creamyTokens';

const { Text } = Typography;

interface ConfirmVisitModalProps {
  request: IServiceRequest;
  open: boolean;
  onClose: () => void;
  onConfirmed: (bookingId: string) => void;
}

/**
 * The professional fixing the visit -- and the moment everything that was
 * deliberately not happening during the negotiation happens at once: the
 * diary is blocked, the booking exists and the customer is asked to pay.
 *
 * Said out loud in the modal, because it is the one irreversible step in this
 * flow and somebody pressing it at seven in the evening deserves to know
 * which of those three things they are about to set off.
 */
const ConfirmVisitModal: React.FC<ConfirmVisitModalProps> = ({ request, open, onClose, onConfirmed }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const accepted = latestServiceQuote(request);
  const [window, setWindow] = useState<[Dayjs, Dayjs] | null>(
    accepted?.proposedFrom && accepted?.proposedTo
      ? [dayjs(accepted.proposedFrom), dayjs(accepted.proposedTo)]
      : null
  );
  // Only asked for when the request never named a listing: a booking line has
  // to name a service, and there is nothing to derive it from.
  const [serviceId, setServiceId] = useState<string | undefined>(request.serviceId || undefined);
  const [services, setServices] = useState<IService[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const needsServicePick = !request.serviceId;

  React.useEffect(() => {
    if (!open || !needsServicePick || services.length > 0) return;
    getMyServices()
      .then((rows) => setServices(rows.filter((s) => s.active)))
      .catch(() => undefined);
  }, [open, needsServicePick, services.length]);

  const handleSubmit = async () => {
    if (!window) {
      message.error(translate(t, TRANSLATIONS.SERVICE_REQUEST_VISIT_WINDOW_REQUIRED));
      return;
    }
    if (needsServicePick && !serviceId) {
      message.error(translate(t, TRANSLATIONS.SERVICE_REQUEST_VISIT_SERVICE_REQUIRED));
      return;
    }

    setSubmitting(true);
    try {
      const booking = await confirmServiceVisit(request.id, {
        startAt: window[0].toISOString(),
        endAt: window[1].toISOString(),
        serviceId,
      });
      message.success(translate(t, TRANSLATIONS.SERVICE_REQUEST_VISIT_CONFIRMED));
      onConfirmed(booking.id);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SERVICE_REQUEST_ACTION_ERROR));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={translate(t, TRANSLATIONS.SERVICE_REQUEST_CONFIRM_VISIT)}
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText={translate(t, TRANSLATIONS.SERVICE_REQUEST_CONFIRM_VISIT_OK)}
      confirmLoading={submitting}
    >
      <div style={{ background: SURFACE_1, borderRadius: 12, padding: 12, marginBottom: 14 }}>
        <Text style={{ fontSize: 13 }}>{translate(t, TRANSLATIONS.SERVICE_REQUEST_AGREED_PRICE)}</Text>
        <Text strong style={{ fontSize: 20, color: TEAL, display: 'block' }}>
          {formatCurrency(accepted?.amount ?? 0, request.currencyCode, i18n.language)}
        </Text>
      </div>

      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
        {translate(t, TRANSLATIONS.SERVICE_REQUEST_WINDOW_LABEL)}
      </Text>
      <DatePicker.RangePicker
        showTime={{ format: 'HH:mm' }}
        value={window}
        onChange={(value) => setWindow(value as [Dayjs, Dayjs] | null)}
        disabledDate={(date) => date.isBefore(dayjs(), 'day')}
        style={{ width: '100%', borderRadius: 12, marginBottom: 14 }}
      />

      {needsServicePick && (
        <>
          <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
            {translate(t, TRANSLATIONS.SERVICE_REQUEST_BILL_UNDER)}
          </Text>
          <Select
            value={serviceId}
            onChange={setServiceId}
            options={services.map((s) => ({ label: s.title, value: s.id }))}
            placeholder={translate(t, TRANSLATIONS.SERVICE_REQUEST_BILL_UNDER_PLACEHOLDER)}
            style={{ width: '100%', marginBottom: 14 }}
          />
        </>
      )}

      <Text type="secondary" style={{ fontSize: 12, display: 'block', color: token.colorTextTertiary }}>
        {translate(t, TRANSLATIONS.SERVICE_REQUEST_CONFIRM_VISIT_WARNING)}
      </Text>
    </Modal>
  );
};

export default ConfirmVisitModal;
